import { useEffect, useMemo, useState } from "react";

import { Alert, Button, Form, Input, Modal, Select } from "antd";
import { EVA_RESEARCH_NAME } from "constants/app.constants";

import usePlayTo3DDataPrivateSources from "hooks/usePlayTo3DDataPrivateSources";

import { getGroupedPlayZonesByPeriod } from "api/map";

import useGeoMapLayers from "components/geo-map/hooks/getGeoMapLayers";
import { MapLayer } from "components/geo-map/models/mapLayer";

import useApplyStickDepths from "./hooks/useApplyStickDepths";
import { VERTICAL_OFFSET_PATTERN } from "./utils/scenarioHelpers";

interface SetStickDepthModalProps {
  open: boolean;
  scenarioId: string;
  onClose: () => void;
}

interface FormValues {
  modelSource: string;
  referenceZone: string;
  verticalOffset: string;
}

export default function SetStickDepthModal({
  open,
  scenarioId,
  onClose
}: SetStickDepthModalProps) {
  const [form] = Form.useForm<FormValues>();
  const [selectedSource, setSelectedSource] = useState("");
  const [ipdbZones, setIpdbZones] = useState<string[]>([]);
  const { data: modelSources } = usePlayTo3DDataPrivateSources();
  const { data: mapLayers } = useGeoMapLayers();
  const { mutateAsync: applyDepths, isLoading, error } = useApplyStickDepths();

  // For EVA Research (default), load zone names directly from IPDB so they
  // match the zone column in the database exactly.
  useEffect(() => {
    let active = true;
    getGroupedPlayZonesByPeriod()
      .then((res) => {
        if (!active) return;
        const zones: string[] = res.data.flatMap((p) =>
          p.plays.flatMap((pl) => pl.zones as string[])
        );
        setIpdbZones([...new Set(zones)].sort());
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const zoneOptions = useMemo(() => {
    if (selectedSource === "") {
      // EVA Research — use IPDB zone names directly
      return ipdbZones.map((n) => ({ label: n, value: n }));
    }
    // Private model source — use map layers filtered by the source's plays
    if (!mapLayers?.length) return [];
    const allowedPlays =
      modelSources?.find((s) => s.source === selectedSource)?.plays ?? [];
    return [
      ...new Set(
        (mapLayers as MapLayer[])
          .filter((l) => allowedPlays.includes(l.group))
          .map((l) => l.name)
      )
    ]
      .sort()
      .map((n) => ({ label: n, value: n }));
  }, [ipdbZones, mapLayers, modelSources, selectedSource]);

  const handleOk = async () => {
    const values = await form.validateFields();
    try {
      await applyDepths({
        scenarioId,
        referenceZone: values.referenceZone,
        verticalOffset: values.verticalOffset,
        zoneSource: selectedSource === "" ? "EVA_RESEARCH" : "ORGANIZATION"
      });
      form.resetFields();
      setSelectedSource("");
      onClose();
    } catch {
      // error is surfaced via useMutation.error and rendered in the Alert above
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedSource("");
    onClose();
  };

  const sourceOptions = [
    { label: EVA_RESEARCH_NAME, value: "" },
    ...(modelSources ?? []).map((s) => ({ label: s.source, value: s.source }))
  ];

  return (
    <Modal
      title="Set Stick Depth"
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" loading={isLoading} onClick={handleOk}>
            Apply
          </Button>
        </div>
      }>
      {error && (
        <Alert
          type="error"
          message={
            (error as { response?: { data?: { error?: string } } })?.response?.data
              ?.error ?? "Failed to apply depths"
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item label="Model Source" name="modelSource" initialValue="">
          <Select
            options={sourceOptions}
            onChange={(v) => {
              setSelectedSource(v);
              form.setFieldValue("referenceZone", undefined);
            }}
          />
        </Form.Item>
        <Form.Item
          label="Zone"
          name="referenceZone"
          rules={[{ required: true, message: "Required" }]}>
          <Select
            showSearch
            placeholder="Select a zone"
            options={zoneOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item
          label="Vertical Offset"
          name="verticalOffset"
          rules={[
            { required: true, message: "Required" },
            {
              pattern: VERTICAL_OFFSET_PATTERN,
              message: "Use format T-40, T+10, B-20, B+0"
            }
          ]}>
          <Input placeholder="e.g. T-40" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
